import { useState, useMemo } from 'react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import RequestsSection from './RequestsSection';
import ApplicationDetailsModal from './ApplicationDetailsModal';
import { PromotionRequest } from '../../../types/promotions';
import { useGetPromotionsQuery } from '../../../store/services/promotionsService';

export default function PromotionsRequestsPage() {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  const [selectedItem, setSelectedItem] = useState<PromotionRequest | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const {
    data: promotionsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetPromotionsQuery({
    page: currentPage,
    limit: pageSize,
    size: pageSize,
  });

  const promotionsList: PromotionRequest[] = useMemo(() => {
    const raw: any = promotionsResponse;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.content)) return raw.content;
    if (Array.isArray(raw.payload)) return raw.payload;
    if (Array.isArray(raw.payload?.content)) return raw.payload.content;
    return [];
  }, [promotionsResponse]);

  const totalElements = useMemo(() => {
    const raw: any = promotionsResponse;
    if (typeof raw?.totalElements === 'number') return raw.totalElements;
    if (typeof raw?.payload?.totalElements === 'number')
      return raw.payload.totalElements;
    if (typeof raw?.payload?.total === 'number') return raw.payload.total;
    if (typeof raw?.total === 'number') return raw.total;
    if (Array.isArray(raw)) return raw.length;
    if (Array.isArray(raw?.content)) return raw.content.length;
    if (Array.isArray(raw?.payload)) return raw.payload.length;
    return promotionsList.length;
  }, [promotionsResponse, promotionsList]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1);
  };

  const handleRowClick = (record: PromotionRequest) => {
    const globalIndex = promotionsList.findIndex(
      (r) => (r.id && r.id === record.id) || r === record
    );
    setSelectedItem(record);
    setSelectedIndex(globalIndex >= 0 ? globalIndex : 0);
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1;
      setSelectedItem(promotionsList[prevIndex]);
      setSelectedIndex(prevIndex);
    }
  };

  const handleNext = () => {
    if (selectedIndex < promotionsList.length - 1) {
      const nextIndex = selectedIndex + 1;
      setSelectedItem(promotionsList[nextIndex]);
      setSelectedIndex(nextIndex);
    }
  };

  const handleActionSuccess = () => {
    refetch();
    setSelectedItem(null);
    setSelectedIndex(-1);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <SectionTitle text={t('home.promotions-requests')} />
          <SectionDesc text={t('home.promotions-requests-desc')} />
        </div>
      </div>

      <RequestsSection
        data={promotionsList}
        isLoading={isLoading || isFetching}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={totalElements}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
      />

      <ApplicationDetailsModal
        isOpen={!!selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setSelectedIndex(-1);
        }}
        item={selectedItem}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={selectedIndex > 0}
        hasNext={
          selectedIndex >= 0 && selectedIndex < promotionsList.length - 1
        }
        onApprove={handleActionSuccess}
        onReject={handleActionSuccess}
        onReturn={handleActionSuccess}
      />
    </div>
  );
}
